import Api from "@/api/Api";
import { REASONABLE_PROJECT_LIST_STEP } from "@/constants";
import { DEFAULT_PROJECTS_PAGE_SIZE } from "@/constants/pagination";
import { GeneratePlanJob } from "@/services/jobPollingService";
import { API_ROUTES, requestTokens } from "@/settings/apiRoutes";
import { PlanFilterState } from "@/settings/userDefaultPlanSettings";
import { AudienceEstimateParams } from "@/store/modules/planCreate";
import { TemplateConfig } from "@/store/modules/projectsTemplates";
import { ProjectCompactAPI } from "@/types/comparison";
import {
  ComparisonApi,
  ComparisonListApiResponse,
  CreateComparisonResponseApi,
  CreateTemplateApi,
  FullLayout,
  Layout,
  UpdateTemplateApi,
} from "@/types/layouts";
import { PlansListResponse } from "@/types/plans";
import {
  GenerateOutputRequestApi,
  MoveProjectNodeInput,
  Project,
  RecalculatePlanInput,
  RecalculatePlanOutput,
  TraditionalChannelMetricsInput,
  TraditionalChannelMetricsOutput,
} from "@/types/project";

export type LayoutPayload = {
  filterState: PlanFilterState;
  icon: string;
  id: string;
  layout: Layout;
};

export type ProjectLayoutResponse = {
  project_id: string;
  payload: LayoutPayload;
  created_at: string;
  updated_at: string;
};

export type GeoExportObject = {
  state: string;
  dma: string;
  city: string;
  zip: string;
};

export type ValidGeoTree = Record<string, Record<string, string[]>>;

export type ValidateGeoResponse = {
  valid_geo_tree?: ValidGeoTree;
  invalid_values?: string[];
};

export default class ProjectsApi {
  /** @deprecated Use ProjectsApi.getInstance() */
  constructor() {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[ProjectsApi] called directly, use getInstance");
    }
  }

  private static _instance: ProjectsApi | null = null;

  static getInstance(): ProjectsApi {
    if (!ProjectsApi._instance) {
      ProjectsApi._instance = new ProjectsApi();
    }
    return ProjectsApi._instance;
  }

  async getProjects(body = {}): Promise<PlansListResponse> {
    return await Api.request<PlansListResponse>({
      path: `${API_ROUTES.PROJECTS.PATH}/list`,
      method: "post",
      token: requestTokens.apiGetProjects,
      body,
      defaultResult: {
        page: 1,
        page_size: DEFAULT_PROJECTS_PAGE_SIZE,
        total: 0,
        next_page: null,
        data: [],
      },
    });
  }

  async getAllProjects(body = {}): Promise<PlansListResponse["data"]> {
    const pageSize = REASONABLE_PROJECT_LIST_STEP;
    let page = 1;
    let allData: PlansListResponse["data"] = [];
    let hasNext = true;

    while (hasNext) {
      const response = await Api.request<PlansListResponse>({
        path: `${API_ROUTES.PROJECTS.PATH}/list`,
        method: "post",
        token: requestTokens.apiGetProjects,
        body: { ...body, page, page_size: pageSize },
        defaultResult: {
          page,
          page_size: pageSize,
          total: 0,
          next_page: null,
          data: [],
        },
      });

      allData = allData.concat(response.data);

      if (response.next_page) {
        page = response.next_page;
      } else {
        hasNext = false;
      }
    }

    return allData;
  }

  async createProject(payload: Project): Promise<Project> {
    return await Api.request({
      path: `${API_ROUTES.PROJECTS.PATH}`,
      method: "post",
      token: requestTokens.apiCreateProject,
      body: payload,
      defaultResult: {} as Project,
    });
  }

  async calculateAudienceSize(payload: AudienceEstimateParams): Promise<Record<string, number>> {
    return await Api.requestWithRetry({
      path: `${API_ROUTES.AUDIENCE_CALCULATE.PATH}`,
      method: "post",
      token: requestTokens.apiAudienceCalculate,
      body: payload,
      defaultResult: {},
      throwError: true,
    });
  }

  async exportGeoData(): Promise<GeoExportObject[]> {
    return await Api.request({
      path: `${API_ROUTES.GEO_EXPORT.PATH}`,
      method: "get",
      token: requestTokens.apiAudienceExport,
      defaultResult: {} as GeoExportObject[],
    });
  }

  async validateGeoData(payload: Record<string, string[]>): Promise<ValidateGeoResponse> {
    return await Api.request({
      path: `${API_ROUTES.GEO_VALIDATE.PATH}`,
      method: "post",
      body: payload,
      token: requestTokens.apiAudienceExport,
      defaultResult: {} as ValidateGeoResponse,
    });
  }

  async updateProject(id: string, payload: Project): Promise<Project> {
    return await Api.request({
      path: `${API_ROUTES.PROJECTS.PATH}/${id}`,
      method: "put",
      token: requestTokens.apiUpdateProject,
      body: payload,
      defaultResult: {} as Project,
      silent: true,
      errorCallback: (_status, error) => {
        throw error;
      },
    });
  }

  async moveProjectNode(id: string, payload: MoveProjectNodeInput): Promise<Project> {
    console.info("[moveProjectNode] request", {
      path: `${API_ROUTES.PROJECTS.PATH}/${id}/move_node`,
      payload,
      payloadJson: JSON.stringify(payload),
    });

    return await Api.request({
      path: `${API_ROUTES.PROJECTS.PATH}/${id}/move_node`,
      method: "post",
      token: requestTokens.apiMoveProjectNode,
      body: payload,
      defaultResult: {} as Project,
      silent: true,
      throwError: true,
    });
  }

  async deleteProject(id: string): Promise<Project> {
    return await Api.request({
      path: `${API_ROUTES.PROJECTS.PATH}/${id}`,
      method: "del",
      token: requestTokens.apiUpdateProject,
      defaultResult: {} as Project,
    });
  }

  async getProjectById<T = Project>(id: string): Promise<T> {
    return await Api.request({
      path: `${API_ROUTES.PROJECTS.PATH}/${id}`,
      method: "get",
      token: requestTokens.apiGetProjectByID,
      defaultResult: {} as T,
    });
  }

  async getProject(id: string): Promise<Project> {
    return await Api.request({
      path: `${API_ROUTES.PROJECTS.PATH}/${id}`,
      method: "get",
      token: requestTokens.apiGetProjectByID,
      defaultResult: {} as Project,
    });
  }

  async getProjectLayout(
    id: string,
    errorCallback?: (statusCode?: number, error?: string) => void,
    retry = false
  ): Promise<ProjectLayoutResponse> {
    const config = {
      path: `${API_ROUTES.PROJECTS.PATH}/${id}/layout`,
      method: "get" as const,
      token: requestTokens.apiGetProjectLayout,
      defaultResult: {} as ProjectLayoutResponse,
      silent: true,
      errorCallback: errorCallback,
    };

    return retry ? await Api.requestWithRetry(config) : await Api.request(config);
  }

  async getComparisonCandidates(): Promise<ProjectCompactAPI> {
    return await Api.request({
      path: `${API_ROUTES.PROJECTS.PATH}/comparison_candidates`,
      method: "get",
      token: requestTokens.apiGetComparisonCandidates,
      defaultResult: {} as unknown as ProjectCompactAPI,
    });
  }

  async createProjectOutputLayout(id: string, payload: FullLayout): Promise<FullLayout> {
    return await Api.request({
      path: `${API_ROUTES.PROJECTS.PATH}/${id}/layout`,
      method: "post",
      token: requestTokens.apiCreateProjectLayout,
      body: { payload: payload },
      defaultResult: {} as FullLayout,
    });
  }

  async updateProjectOutputLayout(id: string, payload: FullLayout): Promise<FullLayout> {
    return await Api.request({
      path: `${API_ROUTES.PROJECTS.PATH}/${id}/layout`,
      method: "put",
      token: requestTokens.apiCreateProjectLayout,
      body: { payload: payload },
      defaultResult: {} as FullLayout,
    });
  }

  async postProjectGenerateOutputAsync(id: string, scenarioIDs: string[]): Promise<GenerateOutputRequestApi> {
    return await Api.request({
      path: `${API_ROUTES.PROJECTS.PATH}/${id}/generate_plan_async`,
      method: "post",
      token: requestTokens.apiPostProjectGenerate,
      body: { scenario_ids: scenarioIDs },
      defaultResult: {} as GenerateOutputRequestApi,
      throwError: true,
    });
  }

  async getProjectGenerateJobById(jobId: string): Promise<GeneratePlanJob> {
    return await Api.request({
      path: `${API_ROUTES.PROJECTS.PATH}/generate_plan_jobs/${jobId}`,
      method: "get",
      token: requestTokens.apiGetProjectGenerateJobStatus,
      defaultResult: {} as GeneratePlanJob,
    });
  }

  async postUpdatePlan(payload: RecalculatePlanInput): Promise<RecalculatePlanOutput> {
    return await Api.requestWithRetry<RecalculatePlanOutput>({
      path: API_ROUTES.UPDATE_PLAN.PATH,
      method: "post",
      token: requestTokens.apiUpdateProjectOutput,
      body: payload,
      defaultResult: { metrics_rows: [], budget_in_time: [], status_summary: [] },
    });
  }

  async calculateTraditionalChannelMetrics(
    payload: TraditionalChannelMetricsInput
  ): Promise<TraditionalChannelMetricsOutput> {
    return await Api.request<TraditionalChannelMetricsOutput>({
      path: API_ROUTES.TRADITIONAL_CHANNEL_METRICS_CALCULATE.PATH,
      method: "post",
      token: requestTokens.apiTraditionalChannelMetrics,
      body: payload,
      defaultResult: {} as TraditionalChannelMetricsOutput,
      throwError: true,
    });
  }

  async approveScenario(scenarioId: string): Promise<unknown> {
    return await Api.request({
      path: `${API_ROUTES.SCENARIOS.PATH}/${scenarioId}/approve`,
      method: "post",
      token: requestTokens.apiScenarioApprove,
      defaultResult: {} as unknown,
    });
  }

  async disapproveScenario(scenarioId: string): Promise<unknown> {
    return await Api.request<unknown>({
      path: `${API_ROUTES.SCENARIOS.PATH}/${scenarioId}/approve`,
      method: "del",
      token: requestTokens.apiScenarioDisapprove,
      defaultResult: {} as unknown,
    });
  }

  async createTemplate(payload: CreateTemplateApi): Promise<TemplateConfig> {
    return await Api.request<TemplateConfig>({
      path: `${API_ROUTES.PLAN_TEMPLATES.PATH}`,
      method: "post",
      body: payload,
      token: requestTokens.apiCreateTemplate,
      defaultResult: {} as TemplateConfig,
    });
  }

  async updateTemplate(payload: UpdateTemplateApi, templateId: string): Promise<UpdateTemplateApi> {
    return await Api.request<UpdateTemplateApi>({
      path: `${API_ROUTES.PLAN_TEMPLATES.PATH}/${templateId}`,
      method: "put",
      body: payload,
      token: requestTokens.apiCreateTemplate,
      defaultResult: {} as UpdateTemplateApi,
    });
  }

  async postScenarioComparison(payload: ComparisonApi): Promise<CreateComparisonResponseApi> {
    return await Api.request<CreateComparisonResponseApi>({
      path: API_ROUTES.SCENARIO_COMPARISONS.PATH,
      method: "post",
      body: payload,
      token: requestTokens.apiCreateTemplate,
      defaultResult: {} as CreateComparisonResponseApi,
    });
  }

  async getScenarioComparisons(): Promise<ComparisonListApiResponse> {
    return await Api.request<ComparisonListApiResponse>({
      path: API_ROUTES.SCENARIO_COMPARISONS.PATH,
      method: "get",
      token: requestTokens.apiGetComparisons,
      defaultResult: {} as ComparisonListApiResponse,
    });
  }
}

export const getProjectsApi = () => ProjectsApi.getInstance();
